import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Container, PageHeader, Section, ActionBar } from '@vnetwork/ui/layout';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateIamRole, s3ExtractError } from '@/api/s3';
import { JsonEditor } from '@/components/JsonEditor';

// Mirror the backend IAM name grammar so errors surface before the round-trip.
const NAME_RE = /^[\w+=,.@-]{1,128}$/;

const DEFAULT_TRUST = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": { "AWS": "*" },
      "Action": "sts:AssumeRole"
    }
  ]
}`;

/** Create an IAM role — name + JSON trust policy, validated client-side. */
export function RoleCreate({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateIamRole(serviceId);
  const listPath = `/services/oss/${serviceId}/iam/roles`;

  const [name, setName] = useState('');
  const [doc, setDoc] = useState(DEFAULT_TRUST);
  const [touched, setTouched] = useState(false);

  const nameError =
    touched && !NAME_RE.test(name)
      ? 'Use 1–128 chars: letters, digits or + = , . @ - _'
      : null;

  const trimmed = doc.trim();
  let docError: string | null = null;
  if (touched) {
    if (!trimmed) docError = 'Trust policy is required.';
    else {
      try {
        JSON.parse(trimmed);
      } catch {
        docError = 'Not valid JSON.';
      }
    }
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!NAME_RE.test(name) || !trimmed) return;
    try {
      JSON.parse(trimmed);
    } catch {
      return;
    }
    create.mutate(
      { role_name: name, assume_role_policy_document: trimmed },
      {
        onSuccess: () => {
          toast.success('Role created');
          navigate(listPath);
        },
        onError: (err) => toast.error(s3ExtractError(err, 'Failed to create role')),
      },
    );
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<Shield size={22} />}
        title="Create role"
        description="Define an IAM role with a JSON trust (assume-role) policy."
      />
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section title="Role" style={{ marginBottom: 0 }}>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ maxWidth: 720 }}>
              <Field label="Name" required error={nameError ?? undefined}>
                {(p) => (
                  <Input
                    {...p}
                    placeholder="e.g. cross-account-reader"
                    value={name}
                    invalid={!!nameError}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => setTouched(true)}
                  />
                )}
              </Field>
            </div>

            <Field label="Trust policy (JSON)" required error={docError ?? undefined}>
              {() => (
                <JsonEditor
                  value={doc}
                  onChange={setDoc}
                  invalid={!!docError}
                  ariaLabel="Trust policy JSON"
                />
              )}
            </Field>
          </div>
        </Section>

        <ActionBar
          start={
            <Button type="button" variant="secondary" disabled={create.isPending} onClick={() => navigate(listPath)}>
              Cancel
            </Button>
          }
          end={
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create role'}
            </Button>
          }
        />
      </form>
    </Container>
  );
}
